import { useEffect, useState, useRef, useMemo } from "react";
import { flushSync } from "react-dom";
import dayjs from "dayjs";
import { Container } from "./style";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Form,
  Input,
  Modal,
  message,
  InputNumber,
  DatePicker,
  Card,
  Collapse,
  Progress,
  Avatar,
  Divider,
  Affix,
} from "antd";
import {
  LeftOutlined,
  CalendarOutlined,
  FileTextOutlined,
  BulbOutlined,
  AimOutlined,
} from "@ant-design/icons";
import ReactRouterPrompt from "react-router-prompt";
import MockPhone from "../../../components/MockPhone/index";
import { gql, useMutation } from "@apollo/client";
import { setChallengeData } from "../../../store/features/CreateChallenge";
import PublishChallengeSteps from "./PublishChallengeSteps";
import { InsightLinkPreview, InsightLinkStatus } from "./InsightLinkPreview";
import { getInitialStartTime } from "./startTime";
import { MAX_CHALLENGE_DAYS } from "../../../utils/const";

const { TextArea } = Input;
const { Panel } = Collapse;

const CREATE_CHALLENGE = gql`
  mutation createChallenge($createChallengeInput: CreateChallengeInput!) {
    createChallenge(payload: $createChallengeInput) {
      _id
    }
  }
`;

const UPDATE_CHALLENGE = gql`
  mutation updateChallenge($updateChallengeInput: UpdateChallengeInput!) {
    updateChallenge(payload: $updateChallengeInput) {
      _id
    }
  }
`;

const DEFAULT_CHALLENGE_BANNER_URL =
  "https://uvita-coach-assets.s3.us-east-2.amazonaws.com/userImages/uvita_default_banner.png";

export default function CreateChallenge() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [collapseActiveKeys, setCollapseActiveKeys] = useState([0]);
  const allowNavigationRef = useRef(false);
  const [createChallenge] = useMutation(CREATE_CHALLENGE);
  const [updateChallenge] = useMutation(UPDATE_CHALLENGE);
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [params] = useSearchParams();
  const location = useLocation();
  const rowData = useSelector((state) => state.challenge.challengeData);
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;
  const challengeId = rowData?._id;
  const isDuplicateMode = location.pathname === "/DuplicateChallenge";
  const isEditMode = location.pathname === "/EditChallenge";
  const isPublishedEdit = isEditMode && rowData?.status === "PUBLISHED";
  const shouldValidateFutureStartTime = !isEditMode;
  const saveButtonLabel = isPublishedEdit ? "Save" : "Save as Draft";

  // Form - 必须在其他使用form的Hook之前定义
  const [form] = Form.useForm();
  const titleValue = Form.useWatch("title", form);
  const numberOfDaysValue = Form.useWatch("numberOfDays", form);
  const isOverMaxDays = numberOfDaysValue > MAX_CHALLENGE_DAYS;
  const scheduledAtValue = Form.useWatch("scheduledAt", form);
  const daysValue = Form.useWatch("days", form);

  // 已发布且 challenge 已开始：锁定开始时间
  const isStartTimeLocked = useMemo(() => {
    if (!isPublishedEdit || !scheduledAtValue) return false;
    return !dayjs().isBefore(dayjs(scheduledAtValue));
  }, [isPublishedEdit, scheduledAtValue]);

  const isDayLocked = (dayIndex) => {
    if (!isPublishedEdit || !scheduledAtValue) return false;
    const dayStart = dayjs(scheduledAtValue).add(dayIndex, "day");
    return !dayjs().isBefore(dayStart);
  };

  useEffect(() => {
    if (location.pathname === "/CreateChallenge" && rowDataRef.current._id) {
      dispatch(setChallengeData({}));
    }
    if (location.pathname === "/EditChallenge" && !rowDataRef.current._id) {
      navigate("/CreateChallenge");
    }
  }, [location, dispatch, navigate]);

  // Update challenge
  useEffect(() => {
    if (form && rowData._id) {
      form.setFieldsValue({
        title: rowData.title,
        numberOfDays: rowData.numberOfDays,
        scheduledAt: getInitialStartTime({
          isDuplicateMode,
          scheduledAt: rowData.scheduledAt,
        }),
        days: (rowData.days || []).map((day) => ({
          reflection:
            typeof day?.prompt === "string"
              ? day.prompt
              : day?.prompt?.content || "",
          insight:
            typeof day?.insight === "string"
              ? day.insight
              : day?.insight?.content || "",
          insightLink: day?.insight?.link || "",
          action:
            typeof day?.action === "string"
              ? day.action
              : day?.action?.content || "",
        })),
      });
    }
  }, [form, rowData, isDuplicateMode]);

  // Set default scheduledAt for new challenges
  useEffect(() => {
    if (form && !challengeId && !rowData?.scheduledAt) {
      const currentValue = form.getFieldValue("scheduledAt");
      if (!currentValue) {
        form.setFieldsValue({
          scheduledAt: dayjs().add(1, "minute"),
        });
      }
    }
  }, [form, challengeId, rowData]);

  // Handle data from AI Challenge page
  useEffect(() => {
    if (form && rowData && !rowData._id && rowData.title) {
      form.setFieldsValue({
        title: rowData.title,
        numberOfDays: rowData.numberOfDays,
        scheduledAt: rowData.scheduledAt ? dayjs(rowData.scheduledAt) : null,
        days: (rowData.days || []).map((day) => ({
          reflection:
            typeof day?.reflection === "string"
              ? day.reflection
              : day?.reflection?.content || "",
          insight:
            typeof day?.insight === "string"
              ? day.insight
              : day?.insight?.content || "",
          action:
            typeof day?.action === "string"
              ? day.action
              : day?.action?.content || "",
        })),
      });
    }
  }, [form, rowData]);

  // Get content from AI tool and fill to the form
  const aiGenerationPrompt = params && params.getAll("aiGenerationPrompt");
  useEffect(() => {
    if (aiGenerationPrompt && aiGenerationPrompt.length > 0 && form) {
      form.setFieldsValue(Object.fromEntries(params.entries()));
    }
  }, [aiGenerationPrompt, form, params]);

  // 自动调整每日内容字段数量
  useEffect(() => {
    if (!form || numberOfDaysValue === undefined) return;

    // 只有当用户输入了有效的天数（1 到 MAX_CHALLENGE_DAYS）时才进行调整
    if (numberOfDaysValue <= 0 || numberOfDaysValue > MAX_CHALLENGE_DAYS) return;

    const targetDays = numberOfDaysValue;
    const currentFields = form.getFieldValue("days") || [];
    const currentDays = currentFields.length;

    if (currentDays !== targetDays) {
      // 提取字段内容的辅助函数
      const extractFieldContent = (field, fieldName) => {
        const value = field?.[fieldName];
        return typeof value === "string" ? value : value?.content || "";
      };

      // 创建字段对象的辅助函数
      const createField = (existingField) => ({
        reflection: extractFieldContent(existingField, "reflection"),
        insight: extractFieldContent(existingField, "insight"),
        insightLink: extractFieldContent(existingField, "insightLink"),
        action: extractFieldContent(existingField, "action"),
      });

      // 创建空字段
      const createEmptyField = () => ({
        reflection: "",
        insight: "",
        insightLink: "",
        action: "",
      });

      // 生成新字段数组
      const newFields = Array.from({ length: targetDays }, (_, index) => {
        const existingField = currentFields[index];
        return existingField ? createField(existingField) : createEmptyField();
      });

      form.setFieldValue("days", newFields);
    }
  }, [numberOfDaysValue, form]);

  const onFormChange = () => {
    setIsChanged(true);
  };

  useEffect(() => {
    if (loading) {
      setIsChanged(false);
    }
  }, [loading]);

  const buildDaysPayload = (values) => {
    const daysWithContent = [];
    for (let i = 0; i < (values.days || []).length; i++) {
      const dayContent = values.days[i] || {};
      const dayNumber = i + 1;
      daysWithContent.push({
        dayNumber,
        order: dayNumber,
        promptContent: dayContent.reflection || "",
        insightContent: dayContent.insight || "",
        insightLink: dayContent.insightLink || "",
        actionContent: dayContent.action || "",
      });
    }
    return daysWithContent;
  };

  const buildSaveInput = (values) => ({
    title: values.title || "",
    numberOfDays: values.numberOfDays || 0,
    scheduledAt: values.scheduledAt ? values.scheduledAt.valueOf() : null,
    days: buildDaysPayload(values),
    status: isPublishedEdit ? "PUBLISHED" : "DRAFTED",
    banner: rowData?.banner || DEFAULT_CHALLENGE_BANNER_URL,
    description: rowData?.description || rowData?.about || "",
    visibility: "PRIVATE",
    visibilityType: "ALL_SUBSCRIBERS",
  });

  const goToContentManager = () => {
    navigate("/contentManager?tabName=4");
  };

  const saveAndExit = async () => {
    const values = form.getFieldsValue();
    // Drafts skip full validation, but never save more than the max days
    if (values.numberOfDays > MAX_CHALLENGE_DAYS) {
      form.validateFields(["numberOfDays"]).catch(() => {});
      return;
    }
    const isEditingExisting =
      isEditMode && !isDuplicateMode && challengeId;

    setLoading(true);
    try {
      const saveInput = buildSaveInput(values);

      if (isEditingExisting) {
        await updateChallenge({
          variables: {
            updateChallengeInput: {
              _id: challengeId,
              coach: userInfo.coach?._id,
              ...saveInput,
            },
          },
        });
      } else {
        await createChallenge({
          variables: {
            createChallengeInput: saveInput,
          },
        });
      }

      message.success(
        isPublishedEdit
          ? "Challenge has been saved!"
          : "Challenge has been saved as draft!"
      );
      dispatch(setChallengeData({}));
      flushSync(() => {
        allowNavigationRef.current = true;
        setIsChanged(false);
      });
      goToContentManager();
    } catch {
      // Apollo handles error display
    } finally {
      setLoading(false);
    }
  };

  const goToPublishStep = (values) => {
    const isEditingExisting =
      isEditMode && !isDuplicateMode && challengeId;

    flushSync(() => {
      allowNavigationRef.current = true;
      setIsChanged(false);
    });

    navigate("/CreateLink", {
      state: {
        challengeDraft: {
          ...(isEditingExisting
            ? { _id: challengeId, status: rowData?.status }
            : {}),
          title: values.title,
          numberOfDays: values.numberOfDays,
          scheduledAt: values.scheduledAt ? values.scheduledAt.valueOf() : null,
          days: buildDaysPayload(values),
          banner: rowData?.banner,
          description: rowData?.description,
          about: rowData?.description || rowData?.about,
          visibility: rowData?.visibility,
          visibilityType: rowData?.visibilityType,
          visibilityGroupId: rowData?.visibilityGroupId,
        },
      },
    });
  };

  const expandErrorPanels = (errorInfo) => {
    const errorKeys = [
      ...new Set(
        (errorInfo?.errorFields || [])
          .map(({ name }) =>
            name[0] === "days" && typeof name[1] === "number" ? name[1] : null
          )
          .filter((i) => i !== null)
      ),
    ];
    if (errorKeys.length > 0) {
      setCollapseActiveKeys(errorKeys);
    }
    // Bring the first invalid field into view (e.g. a past Start Time), after
    // any collapsed day panels above have had a chance to open.
    const firstErrorName = errorInfo?.errorFields?.[0]?.name;
    if (firstErrorName) {
      setTimeout(() =>
        form.scrollToField(firstErrorName, { behavior: "smooth", block: "center" })
      );
    }
  };

  const saveStepOneAndContinue = async (values) => {
    const shouldSaveToServer =
      isEditMode && !isDuplicateMode && challengeId;

    if (shouldSaveToServer) {
      setLoading(true);
      try {
        await updateChallenge({
          variables: {
            updateChallengeInput: {
              _id: challengeId,
              title: values.title,
              numberOfDays: values.numberOfDays,
              status: rowData?.status,
              coach: userInfo.coach?._id,
              scheduledAt: values.scheduledAt
                ? values.scheduledAt.valueOf()
                : null,
              days: buildDaysPayload(values),
            },
          },
        });
        message.success("Challenge has been updated!");
      } catch {
        return;
      } finally {
        setLoading(false);
      }
    }

    goToPublishStep(values);
  };

  const onContinue = async () => {
    try {
      const values = await form.validateFields();
      await saveStepOneAndContinue(values);
    } catch (errorInfo) {
      expandErrorPanels(errorInfo);
    }
  };

  const onBack = () => {
    goToContentManager();
  };

  return (
    <Container forceRender>
      <ReactRouterPrompt when={isChanged}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <Modal
              title="Unsaved changes"
              open
              footer={[
                <div key="flex-between" className="flex-between">
                  <Button
                    key="discard"
                    danger
                    loading={loading}
                    onClick={() => {
                      flushSync(() => {
                        setIsChanged(false);
                      });
                      onConfirm();
                    }}
                  >
                    Discard Changes
                  </Button>
                  <div key="div">
                    <Button key="back" className="mr-10" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button
                      key="save"
                      type="primary"
                      loading={loading}
                      disabled={isOverMaxDays}
                      onClick={() => saveAndExit()}
                    >
                      {saveButtonLabel}
                    </Button>
                  </div>
                </div>,
              ]}
            >
              <p>
                You are about to leave this page with unsaved changes. Would you
                like to save these changes before leaving?
              </p>
            </Modal>
          )
        }
      </ReactRouterPrompt>
      <div className="page-top">
        <PublishChallengeSteps current={0} />
      </div>
      <div className="page-form">
        <div className="left-side">
          <Button
            className="btn-back"
            icon={<LeftOutlined />}
            type="text"
            onClick={onBack}
          >
            Content Manager
          </Button>

          <div className="page-title">
            {location.pathname === "/CreateChallenge"
              ? "Create a new Challenge"
              : location.pathname === "/EditChallenge"
                ? "Update a Challenge"
                : location.pathname === "/DuplicateChallenge"
                  ? "Duplicate Challenge"
                  : ""}
          </div>
          <Form
            form={form}
            name="challengeForm"
            layout="vertical"
            size="large"
            className="full-width"
            initialValues={{
              days: [],
            }}
            onValuesChange={onFormChange}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[
                { required: true, message: "Please input the challenge title" },
              ]}
            >
              <Input
                placeholder="Enter a challenge title"
                showCount
                maxLength={30}
              />
            </Form.Item>
            <Form.Item
              label="Duration"
              name="numberOfDays"
              rules={[
                {
                  required: true,
                  message: "Please input the number of days",
                },
                {
                  type: "number",
                  max: MAX_CHALLENGE_DAYS,
                  message: `Maximum ${MAX_CHALLENGE_DAYS} days`,
                },
              ]}
            >
              <InputNumber
                min={1}
                step={1}
                precision={0}
                placeholder="Enter the number of days"
                style={{ width: "100%" }}
                disabled={isEditMode || isDuplicateMode}
                parser={(value) => {
                  const parsed = parseInt(value?.replace(/\D/g, ""), 10);
                  return isNaN(parsed) || parsed < 1 ? "" : parsed;
                }}
                formatter={(value) => {
                  if (!value) return "";
                  const num = parseInt(value, 10);
                  return isNaN(num) ? "" : String(num);
                }}
              />
            </Form.Item>
            <Form.Item
              label="Start Time"
              name="scheduledAt"
              rules={[
                { required: true, message: "Please select the start time" },
                // Create/Duplicate 需要验证未来时间；Edit 不验证
                ...(shouldValidateFutureStartTime
                  ? [
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        const selectedTime = dayjs(value);
                        const now = dayjs();
                        if (selectedTime.isBefore(now)) {
                          return Promise.reject(
                            new Error("Start time must be in the future")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]
                  : []),
              ]}
            >
              <DatePicker
                showTime={{ format: "HH:mm" }}
                format="MM/DD/YYYY HH:mm"
                style={{ width: "100%" }}
                disabled={isStartTimeLocked}
                disabledDate={(current) => {
                  if (!current) return false;
                  // 禁用今天之前的日期
                  return current && current < dayjs().startOf("day");
                }}
                defaultValue={
                  !challengeId && !rowData?.scheduledAt
                    ? dayjs().add(1, "minute")
                    : undefined
                }
                placeholder="Select a start time. You may change it later."
              />
            </Form.Item>

            {/* Daily Challenge Contents */}
            <Form.Item
              label={
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <CalendarOutlined />
                  <span>Daily Content</span>
                </div>
              }
            >
              <Card
                size="small"
                style={{
                  backgroundColor: "#fafafa",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                }}
              >
                <Form.List name="days">
                  {(fields, { add, remove }) => {
                    const targetDays = numberOfDaysValue || 0;
                    return (
                      <div>
                        <Collapse
                          size="small"
                          activeKey={collapseActiveKeys}
                          onChange={(keys) =>
                            setCollapseActiveKeys(
                              Array.isArray(keys) ? keys : [keys]
                            )
                          }
                        >
                          {fields
                            .slice(0, targetDays)
                            .map((field, dayIndex) => {
                              // 计算具体日期
                              const getDayDate = (dayIndex) => {
                                if (!scheduledAtValue) return "";
                                const startDate = dayjs(scheduledAtValue);
                                const dayDate = startDate.add(dayIndex, "day");
                                return dayDate.format("YYYY-MM-DD");
                              };

                              const dayDate = getDayDate(dayIndex);
                              const headerText = dayDate
                                ? `Day ${dayIndex + 1} (${dayDate})`
                                : `Day ${dayIndex + 1}`;
                              const dayLocked = isDayLocked(dayIndex);

                              return (
                                <Panel
                                  key={dayIndex}
                                  header={headerText}
                                  forceRender
                                >
                                  <div>
                                    {/* Reflection */}
                                    <Form.Item
                                      name={[field.name, "reflection"]}
                                      label="Reflection"
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        {
                                          required: !dayLocked,
                                          whitespace: true,
                                          message: `Please input reflection for Day ${dayIndex + 1}`,
                                        },
                                      ]}
                                    >
                                      <TextArea
                                        placeholder={`Enter reflection for Day ${dayIndex + 1
                                          }`}
                                        autoSize={{ minRows: 6, maxRows: 10 }}
                                        maxLength={500}
                                        showCount
                                        disabled={dayLocked}
                                      />
                                    </Form.Item>

                                    {/* Insight */}
                                    <Form.Item
                                      name={[field.name, "insight"]}
                                      label="Insight"
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        {
                                          required: !dayLocked,
                                          whitespace: true,
                                          message: `Please input insight for Day ${dayIndex + 1}`,
                                        },
                                      ]}
                                    >
                                      <TextArea
                                        rows={6}
                                        placeholder={`Enter insight for Day ${dayIndex + 1
                                          }`}
                                        autoSize={{ minRows: 6, maxRows: 10 }}
                                        maxLength={1000}
                                        showCount
                                        disabled={dayLocked}
                                      />
                                    </Form.Item>

                                    {/* Insight Link */}
                                    <Form.Item
                                      name={[field.name, "insightLink"]}
                                      label="Insight Link"
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input
                                        placeholder={`Enter insight link for Day ${dayIndex + 1
                                          }`}
                                        disabled={dayLocked}
                                      />
                                    </Form.Item>
                                    <InsightLinkStatus
                                      link={daysValue?.[dayIndex]?.insightLink}
                                      initialPreview={rowData.days?.[dayIndex]?.insight?.linkPreview}
                                    />

                                    {/* Action */}
                                    <Form.Item
                                      name={[field.name, "action"]}
                                      label="Action"
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        {
                                          required: !dayLocked,
                                          whitespace: true,
                                          message: `Please input action for Day ${dayIndex + 1}`,
                                        },
                                      ]}
                                    >
                                      <TextArea
                                        rows={6}
                                        className="mb-10"
                                        placeholder={`Enter action for Day ${dayIndex + 1
                                          }`}
                                        autoSize={{ minRows: 6, maxRows: 10 }}
                                        maxLength={500}
                                        showCount
                                        disabled={dayLocked}
                                      />
                                    </Form.Item>
                                  </div>
                                </Panel>
                              );
                            })}
                        </Collapse>

                        {(numberOfDaysValue || 0) === 0 && (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "40px 20px",
                              color: "#999",
                              backgroundColor: "#fafafa",
                              borderRadius: "8px",
                              border: "1px dashed #d9d9d9",
                            }}
                          >
                            <CalendarOutlined
                              style={{ fontSize: "24px", marginBottom: "8px" }}
                            />
                            <div>Please enter the number of days first</div>
                          </div>
                        )}
                      </div>
                    );
                  }}
                </Form.List>
              </Card>
            </Form.Item>
            <br />
            <Form.Item>
              <div className="flex-between" style={{ gap: 16 }}>
                <Button
                  loading={loading}
                  disabled={isOverMaxDays}
                  onClick={() => saveAndExit()}
                  style={{ flex: 1 }}
                >
                  {saveButtonLabel}
                </Button>
                <Button
                  type="primary"
                  loading={loading}
                  disabled={isOverMaxDays}
                  onClick={onContinue}
                  style={{ flex: 1 }}
                >
                  Continue →
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
        <div className="right-side">
          <Affix offsetTop={80}>
            <MockPhone showTabbar isFixed>
              <h3 className="challenge-top">Hello {userInfo?.coach?.name}</h3>
              <div className="mb-10">
                <div className="flex-between">
                  <div>Today's progress</div>
                  <div>0/{numberOfDaysValue || 0}</div>
                </div>
                <Progress percent={0} showInfo={false} />
              </div>
              <div>
                {daysValue &&
                  daysValue.map((day, dayIndex) => {
                    return (
                      <div key={dayIndex}>
                        <Collapse
                          size="small"
                          accordion
                          ghost
                          bordered={false}
                          defaultActiveKey={dayIndex === 0 ? 0 : undefined}
                        >
                          <Panel
                            header={
                              <div>
                                <div className="day-tag">
                                  Day {dayIndex + 1} of {numberOfDaysValue || 0}
                                </div>
                                <h3>{titleValue}</h3>
                                <div className="flex-start">
                                  <Avatar
                                    src={userInfo?.coach?.profile_image}
                                  />
                                  <div className="ml-10 c-gray">
                                    {userInfo?.coach?.name}
                                  </div>
                                </div>
                              </div>
                            }
                          >
                            <div className="content-box">
                              <div className="box-title flex-start">
                                <div className="box-icon blue-icon">
                                  <FileTextOutlined />
                                </div>
                                Daily Reflection
                              </div>
                              <div className="box-content">
                                {typeof day.reflection === "string"
                                  ? day.reflection
                                  : day.reflection?.content || ""}
                              </div>
                              <Button type="primary" block>
                                Write
                              </Button>
                            </div>
                            <div className="content-box">
                              <div className="box-title flex-start">
                                <div className="box-icon yellow-icon">
                                  <BulbOutlined />
                                </div>
                                Today's Insight
                              </div>
                              <div className="box-content">
                                <div className="mb-10">
                                  {typeof day.insight === "string"
                                    ? day.insight
                                    : day.insight?.content || ""}
                                </div>
                                <InsightLinkPreview
                                  link={day.insightLink}
                                  initialPreview={rowData.days?.[dayIndex]?.insight?.linkPreview}
                                />
                              </div>
                              <Button type="primary" block>
                                Mark Complete
                              </Button>
                            </div>
                            <div className="content-box">
                              <div className="box-title flex-start">
                                <div className="box-icon pink-icon">
                                  <AimOutlined />
                                </div>
                                Today's Action
                              </div>
                              <div className="box-content">
                                {typeof day.action === "string"
                                  ? day.action
                                  : day.action?.content || ""}
                              </div>
                              <Button type="primary" block>
                                Mark Complete
                              </Button>
                            </div>
                          </Panel>
                        </Collapse>
                        {dayIndex < daysValue.length - 1 && (
                          <Divider style={{ margin: "8px 0" }} />
                        )}
                      </div>
                    );
                  })}
              </div>
            </MockPhone>
          </Affix>
        </div>
      </div>
    </Container>
  );
}
